import { useEffect, useRef } from 'react';

/**
 * Centralizes custom DOM events used for cross-component communication.
 * Keeps event name strings in one place and provides typed dispatch/listen helpers.
 */

type AppEvent<T = void> = T extends void
  ? { dispatch: () => void; useListener: (handler: () => void) => void }
  : {
      dispatch: (detail: T) => void;
      useListener: (handler: (detail: T) => void) => void;
    };

function useAppEventListener<T>(
  name: string,
  handler: ((detail: T) => void) | (() => void)
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener = (e: Event) => {
      (handlerRef.current as (detail: T) => void)((e as CustomEvent<T>).detail);
    };
    document.addEventListener(name, listener);
    return () => document.removeEventListener(name, listener);
  }, [name]);
}

function createAppEvent(name: string): AppEvent<void>;
function createAppEvent<T>(name: string): AppEvent<T>;
function createAppEvent<T = void>(name: string): AppEvent<T> {
  return {
    dispatch: ((detail?: T) => {
      document.dispatchEvent(new CustomEvent(name, { detail }));
    }) as AppEvent<T>['dispatch'],
    useListener: ((handler: ((detail: T) => void) | (() => void)) => {
      useAppEventListener<T>(name, handler);
    }) as AppEvent<T>['useListener']
  } as AppEvent<T>;
}

/** Opens the global organization selector modal */
export const orgSelectorEvent = createAppEvent('open-org-selector');

/** Fired after the global organization selector modal closes */
export const orgSelectorClosedEvent = createAppEvent('close-org-selector');

/** Opens the global recipe search dialog */
export const globalSearchEvent = createAppEvent('open-global-search');

/** Fired after a user organization is created; detail is the new org ID */
export const userOrgCreatedEvent = createAppEvent<string>('user-org-created');

/** Fired after a user organization is renamed or deleted */
export const userOrgChangedEvent = createAppEvent('user-org-changed');

/** Fired when the org selector button is clicked while the selected org is unavailable */
export const orgSelectorMissingOrgClickEvent = createAppEvent(
  'org-selector-missing-org-click'
);
